/**
 * Estado de Matrículas — sección "Seguimiento" del hub de Inventario de
 * Documentos (transferenciadocumentos.js). Se monta embebida: el hub ya pone
 * la cabecera, por eso aquí no hay PageHeader.
 *
 * Es una pantalla de solo lectura: consulta POST /api/consultarmatricula y
 * usa `sucursales` (labels "codigo-descripcion") y `matriculas2` (un
 * registro por producto con los blobs `sucursaldocumentos` y
 * `cajeromatriculaentregada`). Los blobs se leen SIEMPRE con
 * lib/documentosvehiculo; de ahí se deriva por chasis el estado de entrega,
 * las sucursales que recibieron documentos, los cajeros y las fechas.
 *
 * Los filtros (chasis, sucursal, estado) se aplican en memoria y las cifras
 * de arriba y el PDF (ReporteEstadoMatriculas) se calculan sobre las filas
 * ya filtradas.
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useGlobalMDAlert } from "context";
import { FP, StatCard, StatusPill, FpDataGrid, fpInputSx } from "components/financepro";
import ReporteEstadoMatriculas from "components/ReporteEstadoMatriculas";
import PDFModal from "components/PDFModal";
import { useApi } from "lib/api";
import {
  parseDocumentos,
  parseEntregas,
  labelTipo,
  nombreSucursal,
  codigoSucursal,
} from "lib/documentosvehiculo";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

const ESTADOS_ENTREGA = ["Todas", "Entregada", "No Entregada"];
const TODAS = "Todas";

/**
 * Las fechas de entrega vienen del blob y pueden ser varias separadas por
 * coma; si el valor no es una fecha válida se muestra tal cual en vez de
 * "Invalid Date".
 */
const fmtFecha = (valor) => {
  if (!valor) return "";
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? String(valor) : d.toLocaleDateString();
};

/** Chip por documento del blob: verde si ya se entregó, azul si está recibido en una sucursal. */
const chipDocumento = (doc, i) => {
  const entregado = Boolean(doc.entregado);
  const suc = nombreSucursal(doc.sucursalrecep);
  let label = labelTipo(doc.documento);
  if (entregado) label += " ✓";
  else if (suc) label += ` · ${suc}`;
  return (
    <Chip
      key={i}
      size="small"
      label={label}
      sx={{
        bgcolor: entregado ? "#E8F5E9" : FP.color.infoSoft,
        color: entregado ? "#2E7D32" : "#075985",
        fontWeight: 700,
      }}
    />
  );
};

/** Una fila por producto a partir de matriculas2 (misma derivación de siempre). */
const procesarDatosMatriculas = (datos) =>
  datos.map((item, index) => {
    const documentos = parseDocumentos(item.sucursaldocumentos);
    const entregas = parseEntregas(item.cajeromatriculaentregada);

    const sucursales = [
      ...new Set(
        documentos
          .map((doc) => nombreSucursal(doc.sucursalrecep) || codigoSucursal(doc.sucursalrecep))
          .filter(Boolean)
      ),
    ].join(", ");
    const cajeros = [...new Set(entregas.map((e) => e.cajero).filter(Boolean))].join(",");
    const fechas = [...new Set(entregas.map((e) => e.fecha).filter(Boolean))].join(",");

    const hayDocumentos = documentos.length > 0;
    return {
      id: index + 1,
      chasis: item.chasis || "",
      placa: item.placa || "Sin placa",
      sucursal: item.localizacion || "No especificada",
      // Misma semántica de siempre: hay blob = 'Entregada' (el documento
      // fue movido/recibido en una sucursal), sin blob = 'No Entregada'.
      estadoEntrega: hayDocumentos ? "Entregada" : "No Entregada",
      fechaEntrega: hayDocumentos ? fechas : item.fechamatriculaentregada || "",
      nombreRecibio: hayDocumentos ? cajeros || "" : "No Recibido",
      apellidoRecibio: item.apellidosmatriculaentregada || "",
      cedulaRecibio: item.cedulamatriculaentregada || "",
      sucursalEntrega: hayDocumentos ? sucursales : "No Asignada",
      // Códigos de las sucursales donde se recibió algún documento: es
      // contra lo que filtra el selector de sucursal (por código, nunca por
      // label ni por la localización física, que es un correlativo).
      sucursalesCodigos: [
        ...new Set(documentos.map((doc) => codigoSucursal(doc.sucursalrecep)).filter(Boolean)),
      ],
      cajero: cajeros || "",
      endoso: item.endoso || "",
      documentos,
    };
  });

const columnas = [
  {
    field: "chasis",
    headerName: "Chasis",
    width: 175,
    renderCell: (p) => (
      <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.82rem" }}>
        {p.value}
      </Typography>
    ),
  },
  {
    field: "placa",
    headerName: "Placa",
    width: 120,
    renderCell: (p) => (
      <Typography
        sx={{
          fontSize: "0.84rem",
          fontWeight: p.value === "Sin placa" ? 400 : 700,
          color: p.value === "Sin placa" ? FP.color.textMuted : FP.color.navy,
        }}
      >
        {p.value}
      </Typography>
    ),
  },
  { field: "sucursal", headerName: "Localización", width: 150 },
  {
    field: "estadoEntrega",
    headerName: "Estado entrega",
    width: 150,
    renderCell: (p) => (
      <StatusPill label={p.value} variant={p.value === "Entregada" ? "success" : "warning"} />
    ),
  },
  {
    field: "documentos",
    headerName: "Documentos",
    width: 280,
    sortable: false,
    renderCell: (p) => (
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.5 }}>
        {(p.value || []).map(chipDocumento)}
      </Box>
    ),
  },
  {
    field: "fechaEntrega",
    headerName: "Fecha entrega",
    width: 130,
    renderCell: (p) => (
      <Typography
        sx={{ fontSize: "0.84rem", color: p.value ? FP.color.textPrimary : FP.color.textMuted }}
      >
        {p.value ? fmtFecha(p.value) : "No entregada"}
      </Typography>
    ),
  },
  {
    field: "nombreRecibio",
    headerName: "Recibido por",
    width: 200,
    renderCell: (p) => (
      <Box sx={{ lineHeight: 1.2 }}>
        <Typography sx={{ fontSize: "0.84rem" }}>
          {p.row.nombreRecibio} {p.row.apellidoRecibio}
        </Typography>
        {p.row.cedulaRecibio && (
          <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
            Cédula: {p.row.cedulaRecibio}
          </Typography>
        )}
      </Box>
    ),
  },
  { field: "sucursalEntrega", headerName: "Sucursal entrega", width: 150 },
  { field: "cajero", headerName: "Cajero", width: 150 },
  {
    field: "endoso",
    headerName: "Endoso",
    width: 150,
    renderCell: (p) => (
      <Typography
        sx={{ fontSize: "0.84rem", color: p.value ? FP.color.textPrimary : FP.color.textMuted }}
      >
        {p.value || "No"}
      </Typography>
    ),
  },
];

export default function EstadoMatriculas() {
  const [rows, setRows] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(TODAS);
  const [estadoEntrega, setEstadoEntrega] = useState(TODAS);
  const [busquedaChasis, setBusquedaChasis] = useState("");
  const [cargando, setCargando] = useState(false);
  const [mostrarPDFModal, setMostrarPDFModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const { status, data } = await post("/api/consultarmatricula");
      if (status === 200) {
        setSucursales(data.sucursales || []);
        setRows(procesarDatosMatriculas(data.matriculas2 || []));
      } else {
        showAlert({ message: (data && data.Error) || "Error al cargar los datos", color: "error" });
      }
    } catch (error) {
      showAlert({ message: "Error al conectar con el servidor", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtros en memoria. La sucursal se compara por código (formato mixto
  // "2" / "2-Nombre") contra las sucursales que recibieron documentos del
  // chasis. El legado comparaba contra la localización física con includes(),
  // así que "1" casaba con las ubicaciones 10, 11, 100…
  const datosFiltrados = rows.filter((item) => {
    if (busquedaChasis.trim() && !item.chasis.toLowerCase().includes(busquedaChasis.toLowerCase()))
      return false;
    if (
      sucursalSeleccionada &&
      sucursalSeleccionada !== TODAS &&
      !item.sucursalesCodigos.includes(codigoSucursal(sucursalSeleccionada))
    )
      return false;
    if (estadoEntrega !== TODAS && item.estadoEntrega !== estadoEntrega) return false;
    return true;
  });

  const estadisticas = {
    total: datosFiltrados.length,
    entregadas: datosFiltrados.filter((i) => i.estadoEntrega === "Entregada").length,
    noEntregadas: datosFiltrados.filter((i) => i.estadoEntrega === "No Entregada").length,
  };

  const filtrosReporte = () => ({
    sucursal: sucursalSeleccionada,
    estadoEntrega,
    busquedaChasis,
  });

  const verPDF = () => {
    setPdfUrl(ReporteEstadoMatriculas.generarReporteParaModal(datosFiltrados, filtrosReporte()));
    setMostrarPDFModal(true);
  };

  const descargarPDF = () => {
    ReporteEstadoMatriculas.generarReporte(datosFiltrados, filtrosReporte());
  };

  const sinDatos = cargando || datosFiltrados.length === 0;

  return (
    <Box>
      <Box sx={sectionBox}>
        <SectionTitle icon={FilterAltOutlinedIcon} color={FP.color.navy}>
          Filtros de búsqueda
        </SectionTitle>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="Buscar por chasis"
              placeholder="Número de chasis"
              fullWidth
              value={busquedaChasis}
              onChange={(e) => setBusquedaChasis(e.target.value)}
              sx={{ ...fpInputSx, "& input": { fontFamily: "monospace", fontWeight: 700 } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={[TODAS, ...sucursales.map((s) => s.label)]}
              value={sucursalSeleccionada}
              onChange={(e, v) => setSucursalSeleccionada(v || TODAS)}
              disableClearable
              renderInput={(params) => <TextField {...params} label="Sucursal" sx={fpInputSx} />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={ESTADOS_ENTREGA}
              value={estadoEntrega}
              onChange={(e, v) => setEstadoEntrega(v || TODAS)}
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label="Estado de entrega" sx={fpInputSx} />
              )}
            />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Button
            onClick={cargarDatos}
            disabled={cargando}
            startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            sx={btnOutline(FP.color.navy)}
          >
            {cargando ? "Cargando…" : "Actualizar"}
          </Button>
          <Button
            onClick={verPDF}
            disabled={sinDatos}
            startIcon={<PictureAsPdfIcon />}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Ver PDF
          </Button>
          <Button
            onClick={descargarPDF}
            disabled={sinDatos}
            startIcon={<DownloadOutlinedIcon />}
            sx={btnOutline(FP.color.navy)}
          >
            Descargar PDF
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2.4 }}>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Total matrículas"
            value={estadisticas.total}
            icon={DirectionsCarFilledOutlinedIcon}
            accent={FP.color.navy}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Entregadas"
            value={estadisticas.entregadas}
            icon={CheckCircleOutlineIcon}
            accent={FP.color.success}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="No entregadas"
            value={estadisticas.noEntregadas}
            icon={PendingActionsOutlinedIcon}
            accent={FP.color.warning}
          />
        </Grid>
      </Grid>

      <Box sx={sectionBox}>
        <SectionTitle icon={ListAltOutlinedIcon} color={FP.color.accentBlue}>
          Seguimiento por chasis
        </SectionTitle>
        <FpDataGrid
          autoHeight
          rows={datosFiltrados}
          columns={columnas}
          loading={cargando}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          vacioIcono={DirectionsCarFilledOutlinedIcon}
          vacioTitulo="Sin matrículas para mostrar"
          vacioSubtitulo={
            rows.length === 0
              ? "No hay productos registrados. Use «Actualizar» para volver a consultar."
              : "Ningún chasis coincide con los filtros seleccionados."
          }
        />
      </Box>

      <PDFModal
        open={mostrarPDFModal}
        onClose={() => setMostrarPDFModal(false)}
        pdfUrl={pdfUrl}
        title="Reporte de Estado de Matrículas"
        showDownloadButton
        showPrintButton
      />
    </Box>
  );
}
